/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import React, { useEffect, useState, useRef, ChangeEvent, FormEvent } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Avatar,
  styled,
  Link as MuiLink,
  createTheme,
  Card,
  CardContent,
  CardActionArea,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Badge,
  Tooltip,
  Fab,
  Zoom,
  Fade,
  CardHeader,
  CardMedia,
  CardActions,
  InputAdornment,
  FormHelperText,
  Skeleton,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  ArrowBack,
  School,
  VerifiedUser,
  ChevronRight,
  Logout,
  PersonAdd,
  Print,
  Edit,
  Delete,
  Visibility,
  Close,
  Save,
  Cancel,
  PhotoCamera,
  Upload,
  Refresh,
  CheckCircle,
  ErrorOutline,
  Info,
  Warning,
  CloudUpload,
  AddPhotoAlternate,
  RemoveCircleOutline,
  Face,
  CalendarToday,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import { getUser, baseUrl, requestID, config, getToken, logOutUser } from "../../common/utils";
import axios from "axios";

// ============================================
// Modern Dashboard Theme (MUI5) - Enhanced Professional Theme
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#1e3a5f", // Sophisticated navy
      light: "#3b5a7c",
      dark: "#0f2a44",
    },
    secondary: {
      main: "#dc2626", // Refined red
      light: "#ef4444",
      dark: "#b91c1c",
    },
    success: {
      main: "#059669",
      light: "#10b981",
      dark: "#047857",
    },
    error: {
      main: "#dc2626",
      light: "#f87171",
      dark: "#b91c1c",
    },
    warning: {
      main: "#d97706",
      light: "#f59e0b",
      dark: "#b45309",
    },
    info: {
      main: "#2563eb",
      light: "#3b82f6",
      dark: "#1d4ed8",
    },
    background: {
      default: "#f3f4f6",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#111827",
      letterSpacing: "-0.02em",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "0.025em",
      color: "#9ca3af",
      textTransform: "uppercase",
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    ...Array(19).fill("0 25px 50px -12px rgb(0 0 0 / 0.25)"),
  ],
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.05)",
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
          borderRadius: 8,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
            borderColor: "#1e3a5f",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: 6,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #1e3a5f 0%, #2e4a6f 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #0f2a44 0%, #1e3a5f 100%)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f9fafb",
          color: "#374151",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#e5e7eb",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          "&.Mui-selected": {
            backgroundColor: "#1e3a5f",
            color: "white",
            "&:hover": {
              backgroundColor: "#0f2a44",
            },
          },
        },
      },
    },
  },
});

// ============================================
// Styled Components - Enhanced with Material UI aesthetics
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  background: "linear-gradient(135deg, #1e3a5f 0%, #2e4a6f 100%)",
  padding: "20px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "12px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px",
  color: "#1e3a5f",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "none",
  cursor: "pointer",
  fontSize: "0.875rem",
  transition: "color 0.2s",
  "&:hover": {
    color: "#ffffff",
    textDecoration: "underline",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "rgba(255,255,255,0.1)",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  border: "1px solid rgba(255,255,255,0.2)",
  backdropFilter: "blur(8px)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.3)",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#fafafa",
  },
  "&:hover": {
    backgroundColor: "#f1f5f9 !important",
    transition: "background-color 0.2s",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  backgroundColor: color === "primary" ? "#e3f2fd" : color === "secondary" ? "#e8f5e9" : "#ffebee",
  margin: "0 4px",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: color === "primary" ? "#bbdefb" : color === "secondary" ? "#c8e6c9" : "#ffcdd2",
    transform: "scale(1.05)",
  },
}));

// Types
interface Student {
  id: string;
  schoolId: string;
  firstname: string;
  othername: string;
  lastname: string;
  gender: string;
  schoolType: string;
  dateOfBirth: string;
  passport: string | null;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  lgacode: string;
  year: string;
  studentNumber: string;
  accCode: string;
}

interface EditFormData {
  id: string;
  schoolId: string;
  firstname: string;
  othername: string;
  lastname: string;
  gender: string;
  schoolType: string;
  dateOfBirth: string;
  passport: File | null;
  existingPassport?: string | null;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  studentNumber: string;
}

interface RegistrationFormData {
  schoolId: string;
  firstname: string;
  othername: string;
  lastname: string;
  gender: string;
  schoolType: string;
  dateOfBirth: string;
  passport: File | null;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  year: string;
}

interface MessageState {
  type: 'success' | 'error' | 'info' | 'warning' | '';
  text: string;
}

// View options type
type ViewOption = 'no-passport' | 'with-passport' | 'with-dob';

// Helper Component for Inline Form Fields
interface FormFieldProps {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  md?: number;
}

const FormField = ({ label, children, required, md = 6 }: FormFieldProps) => (
  <Grid item xs={12} md={md}>
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1, sm: 2 },
        mb: 1,
      }}
    >
      <Typography
        sx={{
          minWidth: { sm: 140 },
          fontWeight: 600,
          fontSize: "14px",
          color: "#4b5563",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
        )}
      </Typography>
      <Box sx={{ flex: 1, width: "100%" }}>{children}</Box>
    </Box>
  </Grid>
);

// ============================================
// UPDATED: Score Section Component with 2-character width inputs
// ============================================
interface ScoreSectionProps {
  label: string;
  prefix: string;
  formData: RegistrationFormData;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const ScoreSection = ({ label, prefix, formData, handleChange }: ScoreSectionProps) => {
  // Compact input style for 2-character width
  const compactInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fcfcfc",
      transition: "all 0.2s ease-in-out",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: "2px" },
    },
    "& .MuiInputBase-input": { 
      fontSize: "14px", 
      py: 1.2,
      px: 1,
      textAlign: "center",
    },
    width: "70px",
  };

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "12px",
        backgroundColor: "#f8fafc",
        border: "1px solid #e5e7eb",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "14px",
          color: "#1e3a5f",
          mb: 2,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}>
        {[1, 2, 3].map((term) => (
          <Box key={term} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="caption" sx={{ color: "#6b7280", mb: 0.5, fontSize: "11px" }}>
              Term {term}
            </Typography>
            <TextField
              name={`${prefix}Term${term}`}
              value={formData[`${prefix}Term${term}` as keyof RegistrationFormData] as string}
              onChange={handleChange}
              size="small"
              inputProps={{ 
                maxLength: 2,
                style: { textAlign: "center" }
              }}
              sx={compactInputSx}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ============================================
// UPDATED: Registration Modal Component with 2-character width inputs
// ============================================
interface RegistrationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  schoolCode: string;
  schoolName: string;
  lgaCode: string;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({
  open,
  onClose,
  onSuccess,
  schoolCode,
  schoolName,
  lgaCode,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [passportPreview, setPassportPreview] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const initialState: RegistrationFormData = {
    schoolId: schoolCode,
    firstname: "",
    othername: "",
    lastname: "",
    gender: "",
    schoolType: "",
    dateOfBirth: "",
    passport: null,
    englishTerm1: "",
    englishTerm2: "",
    englishTerm3: "",
    arithmeticTerm1: "",
    arithmeticTerm2: "",
    arithmeticTerm3: "",
    generalTerm1: "",
    generalTerm2: "",
    generalTerm3: "",
    religiousType: "",
    religiousTerm1: "",
    religiousTerm2: "",
    religiousTerm3: "",
    year: "2026/2027",
  };

  const [formData, setFormData] = useState<RegistrationFormData>({ ...initialState });

  // UPDATED: Handle change with validation for score fields (max 2 digits, max 99)
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Check if this is a score field (contains 'Term')
    if (name.includes('Term')) {
      // Allow empty string or numbers with max 2 digits and <= 99
      if (value === '' || (/^\d{0,2}$/.test(value) && parseInt(value) <= 99)) {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      
      if (!validTypes.includes(file.type)) {
        setMessage("Invalid file type. Please upload JPG, PNG, or GIF.");
        return;
      }
      
      if (file.size > maxSize) {
        setMessage("File too large. Maximum size is 2MB.");
        return;
      }
      
      setFormData({ ...formData, passport: file });
      const reader = new FileReader();
      reader.onloadend = () => setPassportPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removePassport = () => {
    setFormData({ ...formData, passport: null });
    setPassportPreview(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const fd = new FormData();

      fd.append("lgaCode", lgaCode);
      fd.append("schoolcode", schoolCode);
      fd.append("requestID", requestID());
      console.log("Submitting with schoolCode prop:", schoolCode);

      Object.keys(formData).forEach((key) => {
        const value = formData[key as keyof RegistrationFormData];

        if (value !== null && value !== undefined && value !== "") {
          if (key === 'passport' && value instanceof File) {
            fd.append(key, value);
          } else if (typeof value === 'string') {
            fd.append(key, value);
          }
        }
      });

      const res = await axios.post(
        `${baseUrl()}schools/registerStudent`,
        fd,
        config
      );
      
      if (res.data.status) {
        setMessage("Student Registered Successfully");
        setFormData(initialState);
        setPassportPreview(null);
        if (formRef.current) {
          formRef.current.reset();
        }

        // Call onSuccess after a short delay to show success message
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setMessage(res.data.message || "Registration failed");
      }

    } catch (err) {
      console.error(err);
      setMessage("Server error occurred");
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fcfcfc",
      transition: "all 0.2s ease-in-out",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: "2px" },
    },
    "& .MuiInputBase-input": { fontSize: "14px", py: 1.5 },
  };

  // UPDATED: Compact style for religious term inputs
  const compactInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fcfcfc",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: "2px" },
    },
    "& .MuiInputBase-input": { 
      fontSize: "14px", 
      py: 1.2,
      px: 1,
      textAlign: "center",
    },
    width: "70px",
  };

  const selectSx = {
    borderRadius: "10px",
    fontSize: "14px",
    backgroundColor: "#fcfcfc",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1e3a5f",
      borderWidth: "2px",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Zoom}
      transitionDuration={300}
    >
      <DialogTitle sx={{ 
        bgcolor: "#1e3a5f", 
        color: "white",
        py: 2,
        px: 3,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonAdd sx={{ fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Register New Student</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4, maxHeight: "80vh", overflow: "auto" }}>
        {message && (
          <Alert
            severity={message.includes("Successfully") ? "success" : "error"}
            sx={{
              mb: 3,
              borderRadius: "12px",
            }}
          >
            {message}
          </Alert>
        )}

        <form ref={formRef} onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1.5 }}>
              <Box sx={{ width: 4, height: 24, backgroundColor: "#1e3a5f", borderRadius: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#111827" }}>
                Personal Information
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <FormField label="Last Name" required>
                <TextField
                  name="lastname"
                  placeholder="Enter surname"
                  value={formData.lastname}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </FormField>

              <FormField label="First Name" required>
                <TextField
                  name="firstname"
                  placeholder="Enter first name"
                  value={formData.firstname}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </FormField>

              <FormField label="Other Name">
                <TextField
                  name="othername"
                  placeholder="Enter other name (optional)"
                  value={formData.othername}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </FormField>

              <FormField label="School Type" required>
                <FormControl fullWidth size="small">
                  <Select
                    name="schoolType"
                    value={formData.schoolType}
                    onChange={handleSelectChange}
                    sx={selectSx}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select school type</MenuItem>
                    <MenuItem value="Private">Private</MenuItem>
                    <MenuItem value="Public">Public</MenuItem>
                  </Select>
                </FormControl>
              </FormField>

              <FormField label="Gender" required>
                <FormControl fullWidth size="small">
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleSelectChange}
                    sx={selectSx}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
              </FormField>

              <FormField label="Date of Birth" required>
                <TextField
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
              </FormField>

              {/* Passport Upload */}
              <FormField label="Passport Photo" md={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                  {!passportPreview ? (
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        borderStyle: "dashed",
                        borderWidth: "2px",
                        borderRadius: "12px",
                        px: 4,
                        py: 1.5,
                        borderColor: "#d1d5db",
                        color: "#4b5563",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#1e3a5f",
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <CloudUpload sx={{ mr: 1 }} /> Click to Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Box
                        component="img"
                        src={passportPreview}
                        alt="Passport preview"
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "8px",
                          objectFit: "cover",
                          border: "2px solid #fff",
                        }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                          Image Uploaded
                        </Typography>
                        <Button
                          size="small"
                          onClick={removePassport}
                          startIcon={<Delete />}
                          sx={{
                            p: 0,
                            minWidth: "auto",
                            fontSize: "12px",
                            color: "#ef4444",
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  )}
                  <Typography sx={{ fontSize: "12px", color: "#9ca3af", maxWidth: 200 }}>
                    JPG, PNG or GIF. Max size 2MB.
                  </Typography>
                </Box>
              </FormField>
            </Grid>
          </Box>

          <Divider sx={{ my: 4, borderColor: "#e5e7eb" }} />

          {/* UPDATED: Academic Performance Section with compact 2-character inputs */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 1.5 }}>
              <Box sx={{ width: 4, height: 24, backgroundColor: "#1e3a5f", borderRadius: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#111827" }}>
                Academic Performance (Continuous Assessment)
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <ScoreSection
                  label="English Studies"
                  prefix="english"
                  formData={formData}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ScoreSection
                  label="Arithmetic"
                  prefix="arithmetic"
                  formData={formData}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ScoreSection
                  label="General Paper"
                  prefix="general"
                  formData={formData}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#1e3a5f",
                      mb: 2,
                    }}
                  >
                    Religious Education
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <Select
                          name="religiousType"
                          value={formData.religiousType}
                          onChange={handleSelectChange}
                          sx={selectSx}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>Select Type</MenuItem>
                          <MenuItem value="Islam">Islamic Studies</MenuItem>
                          <MenuItem value="Christian">Christian Studies</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {[1, 2, 3].map((term) => (
                          <Box key={term} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ color: "#6b7280", mb: 0.5, fontSize: "11px" }}>
                              Term {term}
                            </Typography>
                            <TextField
                              name={`religiousTerm${term}`}
                              value={formData[`religiousTerm${term}` as keyof RegistrationFormData] as string}
                              onChange={handleChange}
                              size="small"
                              inputProps={{ 
                                maxLength: 2,
                                style: { textAlign: "center" }
                              }}
                              sx={compactInputSx}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #e5e7eb", gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<Cancel />}
          sx={{
            textTransform: "none",
            borderColor: "#d1d5db",
            color: "#374151",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f3f4f6",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : <Save />}
          disabled={loading}
          sx={{
            bgcolor: "#1e3a5f",
            "&:hover": { bgcolor: "#0f2a44" },
            textTransform: "none",
            px: 4,
          }}
        >
          {loading ? "Registering..." : "Register Student"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Main RegisteredStudents Component
// ============================================
const RegisteredStudents: React.FC = () => {
  const history = useHistory();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Get user data from utils
  const user = getUser();
  console.log(user)
  
  const schoolData = {
    schoolName: user?.schoolName || "",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });
  const [registrationStatus, setRegistrationStatus] = useState<0 | 1>(1); // 1 = OPEN, 0 = CLOSED
  
  // View option state
  const [viewOption, setViewOption] = useState<ViewOption>('no-passport');
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [registerModalOpen, setRegisterModalOpen] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  
  // Image preview for edit modal
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState<EditFormData>({
    id: "",
    schoolId: "",
    firstname: "",
    othername: "",
    lastname: "",
    gender: "",
    schoolType: "",
    dateOfBirth: "",
    passport: null,
    existingPassport: null,
    englishTerm1: "",
    englishTerm2: "",
    englishTerm3: "",
    arithmeticTerm1: "",
    arithmeticTerm2: "",
    arithmeticTerm3: "",
    generalTerm1: "",
    generalTerm2: "",
    generalTerm3: "",
    religiousType: "",
    religiousTerm1: "",
    religiousTerm2: "",
    religiousTerm3: "",
    studentNumber: "",
  });

  // Fetch students
  const fetchStudents = async () => {
    setLoading(true);
    try {
      if (!user) throw new Error("No user data");

      const res = await axios.post(
        `${baseUrl()}schools/getStudents`,
        {
          lgaCode: user.lgaCode,
          schCode: user.schoolCode,
          requestID: requestID(),
        },
        config
      );

      if (res.data.status) {
        setStudents(res.data.data.students || []);
      } else {
        setMessage({ type: "error", text: res.data.message || "Failed to fetch students" });
      }
    } catch (error) {
      console.log("Fetch error:", error);
      setMessage({ type: "error", text: "Error fetching students" });
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrationStatus = async () => {
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/getSettings`, {});
      if (res.data.status && res.data.data) {
        // Map 1 to Open, 2 to Closed
        setRegistrationStatus(res.data.data.status === "1" ? 1 : 0);
      }
    } catch (error) {
      console.error("Error fetching registration status:", error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchRegistrationStatus();
  }, []);

  // Handle logout
  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    history.push("/school_dashboard");
  };

  // Handle register new student - open modal instead of navigation
  const handleRegisterStudent = () => {
    setRegisterModalOpen(true);
  };

  // Handle successful registration
  const handleRegistrationSuccess = () => {
    fetchStudents();
    setMessage({ type: "success", text: "Student registered successfully!" });
    setTimeout(() => setMessage({ type: '', text: "" }), 3000);
  };

  // Handle view option change
  const handleViewOptionChange = (
    event: React.MouseEvent<HTMLElement>,
    newOption: ViewOption | null,
  ) => {
    if (newOption !== null) {
      setViewOption(newOption);
    }
  };

  // Enhanced print function
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    // Get the current date for the print header
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Determine which columns to show based on view option
    const showPassport = viewOption === 'with-passport';
    const showDOB = viewOption === 'with-dob';

    // Generate the HTML content for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Registered Students - ${schoolData.schoolName}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: 'Inter', 'Roboto', 'Arial', sans-serif;
          }
          
          body {
            padding: 30px;
            background: white;
            color: #111827;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #1e3a5f;
          }
          
          .print-header h1 {
            color: #1e3a5f;
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
            letter-spacing: -0.5px;
          }
          
          .print-header h2 {
            color: #374151;
            font-size: 18px;
            font-weight: 500;
            margin-bottom: 5px;
          }
          
          .print-header .school-info {
            color: #6b7280;
            font-size: 14px;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #d1d5db;
          }
          
          .print-date {
            color: #6b7280;
            font-size: 12px;
            margin-top: 10px;
          }
          
          .view-option-badge {
            display: inline-block;
            margin-top: 10px;
            padding: 4px 12px;
            background: #f3f4f6;
            border-radius: 20px;
            font-size: 12px;
            color: #1e3a5f;
            font-weight: 600;
            border: 1px solid #d1d5db;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 12px;
          }
          
          th {
            background: #1e3a5f;
            color: white;
            font-weight: 600;
            padding: 12px 8px;
            text-align: left;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          td {
            padding: 10px 8px;
            border-bottom: 1px solid #e5e7eb;
            color: #374151;
          }
          
          tr:nth-child(even) {
            background-color: #f9fafb;
          }
          
          .gender-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
          }
          
          .gender-male {
            background: #e3f2fd;
            color: #1976d2;
          }
          
          .gender-female {
            background: #fce4ec;
            color: #c2185b;
          }
          
          .school-type-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            background: #f3f4f6;
            color: #374151;
            font-size: 11px;
            font-weight: 500;
            border: 1px solid #d1d5db;
          }
          
          .access-code {
            font-family: monospace;
            background: #f3f4f6;
            padding: 4px 6px;
            border-radius: 4px;
            font-size: 11px;
            color: #1e3a5f;
          }
          
          .passport-placeholder {
            width: 30px;
            height: 30px;
            background: #f3f4f6;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            color: #1e3a5f;
            border: 2px solid #e5e7eb;
          }
          
          .passport-image {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid #e5e7eb;
          }
          
          .print-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 11px;
          }
          
          .print-footer p {
            margin: 5px 0;
          }
          
          .page-break {
            page-break-after: always;
          }
          
          .summary-stats {
            margin: 20px 0;
            padding: 15px;
            background: #f9fafb;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            display: flex;
            gap: 30px;
            flex-wrap: wrap;
          }
          
          .stat-item {
            display: flex;
            flex-direction: column;
          }
          
          .stat-label {
            font-size: 11px;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .stat-value {
            font-size: 18px;
            font-weight: 700;
            color: #1e3a5f;
            margin-top: 4px;
          }
          
          .no-data {
            text-align: center;
            padding: 50px;
            color: #9ca3af;
            font-style: italic;
          }
          
          @media print {
            body {
              padding: 0.5in;
            }
            
            th {
              background: #1e3a5f !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .gender-male {
              background: #e3f2fd !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .gender-female {
              background: #fce4ec !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          
          .watermark {
            position: fixed;
            bottom: 20px;
            right: 20px;
            opacity: 0.1;
            font-size: 60px;
            font-weight: bold;
            color: #1e3a5f;
            transform: rotate(-15deg);
            pointer-events: none;
            z-index: 1000;
          }
        </style>
      </head>
      <body>
        <div class="watermark">OFFICIAL COPY</div>
        
        <div class="print-header">
          <h1>DELTA STATE MINISTRY OF PRIMARY EDUCATION</h1>
          <h2>REGISTERED STUDENTS REPORT</h2>
          <h3>${schoolData.schoolName}</h3>
          <div class="school-info">
            LGA Code: ${schoolData.lgaCode} | School Code: ${schoolData.schoolCode}
          </div>
          <div class="view-option-badge">
            View: ${viewOption === 'no-passport' ? 'Without Passport' : viewOption === 'with-passport' ? 'With Passport' : 'With Date of Birth'}
          </div>
          <div class="print-date">
            Printed on: ${currentDate}
          </div>
        </div>

        <!-- Summary Statistics -->
        <div class="summary-stats">
          <div class="stat-item">
            <span class="stat-label">Total Students</span>
            <span class="stat-value">${students.length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Male Students</span>
            <span class="stat-value">${students.filter(s => s.gender === 'male').length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Female Students</span>
            <span class="stat-value">${students.filter(s => s.gender === 'female').length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Private School</span>
            <span class="stat-value">${students.filter(s => s.schoolType === 'private').length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Public School</span>
            <span class="stat-value">${students.filter(s => s.schoolType === 'Public').length}</span>
          </div>
        </div>

        <!-- Students Table -->
        ${students.length > 0 ? `
          <table>
            <thead>
              <tr>
                <th>S/N</th>
                ${showPassport ? '<th>Photo</th>' : ''}
                <th>Student Number</th>
                <th>Full Name</th>
                <th>Gender</th>
                 ${showDOB ? '<th>Date of Birth</th>' : ''}
                <th>Religion</th>
              </tr>
            </thead>
            <tbody>
              ${students.map((student, index) => {
                // Format date of birth if available
                const formattedDOB = student.dateOfBirth 
                  // ? new Date(student.dateOfBirth).toLocaleDateString('en-US', {
                  //     year: 'numeric',
                  //     month: 'short',
                  //     day: 'numeric'
                  //   })
                  // : 'N/A';
                
                // Get initials for passport placeholder

                // src={selectedStudent.passport ? `${baseUrl()}/assets/img/students/${selectedStudent.studentNumber}` : `${baseUrl()}/assets/${selectedStudent.passport}`}

                const initials = `${student.firstname?.[0] || ''}${student.lastname?.[0] || ''}`;
                
                return `
                  <tr>
                    <td>${index + 1}</td>
                    ${showPassport ? `
                      <td>
                        ${student.passport 
                          ? `<img src="${baseUrl()}assets/${student.passport}" alt="Passport" class="passport-image" />`
                          : `<img src="${baseUrl()}assets/img/students/${student.studentNumber}.jpg" alt="Passport" class="passport-image" />`
                        }
                      </td>
                    ` : ''}
                    <td><strong>${student.studentNumber}</strong></td>
                    <td>${student.lastname} ${student.firstname} ${student.othername || ''}</td>
                    <td>
                      <span class="gender-badge ${student.gender === 'male' ? 'gender-male' : 'gender-female'}">
                        ${student.gender}
                      </span>
                    </td>
                   
                    ${showDOB ? `<td>${student.dateOfBirth}</td>` : ''}
                    <td>
                      <span class="access-code">${Number(student.religiousType) === 1 || student.religiousType === 'Christian' ? 'Christian' : 'Islam'}</span>
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        ` : `
          <div class="no-data">
            <p>No students registered yet.</p>
          </div>
        `}

        <!-- Detailed Statistics -->
        <div style="margin-top: 40px;">
          <h4 style="color: #1e3a5f; margin-bottom: 15px;">REGISTRATION SUMMARY</h4>
          <table style="width: 50%; margin: 0;">
            <tr>
              <td><strong>Total Registered Students:</strong></td>
              <td>${students.length}</td>
            </tr>
            <tr>
              <td><strong>Male Students:</strong></td>
              <td>${students.filter(s => s.gender === 'male').length}</td>
            </tr>
            <tr>
              <td><strong>Female Students:</strong></td>
              <td>${students.filter(s => s.gender === 'female').length}</td>
            </tr>
            <tr>
              <td><strong>Private Schools:</strong></td>
              <td>${students.filter(s => s.schoolType === 'private').length}</td>
            </tr>
            <tr>
              <td><strong>Public Schools:</strong></td>
              <td>${students.filter(s => s.schoolType === 'public').length}</td>
            </tr>
          </table>
        </div>

        <!-- Signature Section -->
        <div style="margin-top: 60px; display: flex; justify-content: space-between;">
          <div style="text-align: center; width: 200px;">
            <div style="margin-bottom: 40px;">_________________________</div>
            <div><strong>Principal's Signature</strong></div>
            <div style="color: #6b7280; font-size: 11px; margin-top: 5px;">Name: ________________</div>
            <div style="color: #6b7280; font-size: 11px;">Date: _________________</div>
          </div>
          <div style="text-align: center; width: 200px;">
            <div style="margin-bottom: 40px;">_________________________</div>
            <div><strong>Head Teacher's Signature</strong></div>
            <div style="color: #6b7280; font-size: 11px; margin-top: 5px;">Name: ________________</div>
            <div style="color: #6b7280; font-size: 11px;">Date: _________________</div>
          </div>
        </div>

        <div class="print-footer">
          <p>This is an official document of the Delta State Ministry of Primary Education</p>
          <p>Generated on: ${currentDate}</p>
          <p>Document ID: ${new Date().getTime()}</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    // Write the content to the new window
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Handle view student
  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setViewModalOpen(true);
  };

  // Handle edit student - Enhanced with image support
  const handleEditStudent = (student: Student) => {
    setSelectedStudent(student);
    setEditForm({
      id: student.id,
      schoolId: student.schoolId,
      firstname: student.firstname,
      othername: student.othername || "",
      lastname: student.lastname,
      gender: student.gender,
      schoolType: student.schoolType,
      dateOfBirth: student.dateOfBirth,
      passport: null,
      existingPassport: student.passport,
      englishTerm1: student.englishTerm1,
      englishTerm2: student.englishTerm2,
      englishTerm3: student.englishTerm3,
      arithmeticTerm1: student.arithmeticTerm1,
      arithmeticTerm2: student.arithmeticTerm2,
      arithmeticTerm3: student.arithmeticTerm3,
      generalTerm1: student.generalTerm1,
      generalTerm2: student.generalTerm2,
      generalTerm3: student.generalTerm3,
      religiousType: student.religiousType,
      religiousTerm1: student.religiousTerm1,
      religiousTerm2: student.religiousTerm2,
      religiousTerm3: student.religiousTerm3,
      studentNumber: student.studentNumber,
    });
    // Set image preview from existing passport
    if (student.passport) {
      setImagePreview(student.passport
                                      ? `${baseUrl()}assets/img/students/${student.passport}`
                                      : `${baseUrl()}assets/img/students/${student.studentNumber}.jpg`);
    } else {
      setImagePreview(null);
    }
    setEditModalOpen(true);
  };

  // Handle delete student
  const handleDeleteStudent = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;

    try {
      if (!user) throw new Error("No user data");

      const res = await axios.post(
        `${baseUrl()}schools/deleteStudent`,
        {
          studentId: id,
          schCode: user.schoolCode,
          lgaCode: user.lgaCode,
          requestID: requestID(),
        },
        config
      );

      if (res.data.status) {
        setStudents((prev) => prev.filter((student) => student.id !== id));
        setMessage({ type: "success", text: "Student deleted successfully" });
        setTimeout(() => setMessage({ type: '', text: "" }), 3000);
      } else {
        alert(res.data.message);
      }
    } catch (error) {
      console.log("Delete error:", error);
      setMessage({ type: "error", text: "Error deleting student" });
    }
  };

  // Handle edit form change
  const handleEditChange = (field: keyof EditFormData, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  // Handle file change for passport upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      
      if (!validTypes.includes(file.type)) {
        setMessage({ type: "error", text: "Invalid file type. Please upload JPG, PNG, or GIF." });
        return;
      }
      
      if (file.size > maxSize) {
        setMessage({ type: "error", text: "File too large. Maximum size is 2MB." });
        return;
      }
      
      setEditForm((prev) => ({ ...prev, passport: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setEditForm((prev) => ({ ...prev, passport: null }));
    setImagePreview(editForm.existingPassport ? `${baseUrl()}/assets/img/students/${editForm.existingPassport}` : null);
  };

  // Handle edit submit with image upload
  const handleEditSubmit = async () => {
    try {
      if (!user) throw new Error("No user data");
      setUploadLoading(true);

      const formData = new FormData();
      
      // Append all form fields
      Object.keys(editForm).forEach((key) => {
        const value = editForm[key as keyof EditFormData];
        if (key !== 'existingPassport' && key !== 'passport' && value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });
      
      // Append passport if new image selected
      if (editForm.passport) {
        formData.append('passport', editForm.passport);
      }
      
      formData.append('lgaCode', user.lgaCode);
      formData.append('requestID', requestID());

      const res = await axios.post(
        `${baseUrl()}schools/updateStudent`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (res.data.status) {
        // Refresh students list to get updated data
        await fetchStudents();
        setEditModalOpen(false);
        setMessage({ type: "success", text: "Student updated successfully" });
        setTimeout(() => setMessage({ type: '', text: "" }), 3000);
      } else {
        setMessage({ type: "error", text: res.data.message || "Failed to update student" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Error updating student" });
    } finally {
      setUploadLoading(false);
    }
  };

  const getGenderColor = (gender: string) => {
    return gender === "Male" ? "#2196f3" : "#e91e63";
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          {/* Full page layout */}
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Enhanced with gradient */}
            <Header>
              <HeaderTopRow>
                {/* Left side - Logo and Ministry info */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", letterSpacing: "0.5px", display: "block", mb: 0.5 }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2, mb: 0.25 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Student Records Management
                    </Typography>
                  </Box>
                </Box>

                {/* Right side - School info */}
                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase", mb: 0.5 }}
                  >
                    {schoolData.schoolName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                    LGA: {schoolData.lgaCode} | School Code: {schoolData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              {/* Bottom row - Logout button */}
              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>
            </Header>

            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
              {/* Back to Dashboard Button */}
              <Button
                onClick={handleBackToDashboard}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#1e3a5f",
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back to Dashboard
              </Button>

              {/* Header Section with Actions and View Options */}
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e3a5f", mb: 1 }}>
                      Registered Students
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View and manage all registered primary school students in your school
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {registrationStatus === 1 ? (
                      <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={handleRegisterStudent}
                        sx={{
                          bgcolor: "#059669",
                          "&:hover": { bgcolor: "#047857" },
                          textTransform: "none",
                          px: 3,
                          py: 1,
                        }}
                      >
                        Register Student
                      </Button>
                    ) : (
                      <Alert
                        severity="warning"
                        sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff4e5', color: '#7c4d00', borderColor: '#ffe0b2', py: 1, px: 2, m: 0 }}
                      >
                        Registration is currently closed. New students cannot be registered.
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      startIcon={<Print />}
                      onClick={handlePrint}
                      sx={{
                        bgcolor: "#1e3a5f",
                        "&:hover": { bgcolor: "#0f2a44" },
                        textTransform: "none",
                        px: 3,
                        py: 1,
                      }}
                    >
                      Print List
                    </Button>
                  </Box>
                </Box>

                {/* View Options Toggle */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#4b5563" }}>
                    Display Options:
                  </Typography>
                  <ToggleButtonGroup
                    value={viewOption}
                    exclusive
                    onChange={handleViewOptionChange}
                    size="small"
                    sx={{
                      bgcolor: "#f9fafb",
                      "& .MuiToggleButton-root": {
                        px: 3,
                        py: 1,
                      },
                    }}
                  >
                    <ToggleButton value="no-passport">
                      <BadgeIcon sx={{ mr: 1, fontSize: 18 }} />
                      Without Passport
                    </ToggleButton>
                    {/* <ToggleButton value="with-passport">
                      <Face sx={{ mr: 1, fontSize: 18 }} />
                      With Passport
                    </ToggleButton>
                    <ToggleButton value="with-dob">
                      <CalendarToday sx={{ mr: 1, fontSize: 18 }} />
                      With Date of Birth
                    </ToggleButton> */}
                  </ToggleButtonGroup>
                </Box>
              </Paper>

              {/* Message Alert */}
              {message.text && (
                <Fade in={true}>
                  <Alert 
                    severity={message.type === 'success' ? 'success' : message.type === 'error' ? 'error' : 'info'} 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                    onClose={() => setMessage({ type: '', text: '' })}
                    icon={message.type === 'success' ? <CheckCircle /> : message.type === 'error' ? <ErrorOutline /> : <Info />}
                  >
                    {message.text}
                  </Alert>
                </Fade>
              )}

              {/* Students Table */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={48} thickness={4} sx={{ color: "#1e3a5f" }} />
                </Box>
              ) : (
                <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {viewOption === 'with-passport' && <TableCell><b>Photo</b></TableCell>}
                          <TableCell><b>Student Number</b></TableCell>
                          <TableCell><b>Full Name</b></TableCell>
                          <TableCell><b>Gender</b></TableCell>
                          <TableCell><b>School Type</b></TableCell>
                          {viewOption === 'with-dob' && <TableCell><b>Date of Birth</b></TableCell>}
                          <TableCell><b>Religion</b></TableCell>
                          {/* <TableCell><b>Access Code</b></TableCell> */}
                          <TableCell align="center"><b>Actions</b></TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {students.map((student) => (
                          <StyledTableRow key={student.id}>
                            {viewOption === 'with-passport' && (
                              <TableCell>
                                <Avatar
                                  src={
                                    student.passport
                                      ? `${baseUrl()}assets/${student.passport}`
                                      : `${baseUrl()}assets/img/students/${student.studentNumber}.jpg`
                                  }
                                  sx={{ 
                                    width: 45, 
                                    height: 45,
                                    border: "2px solid #e5e7eb",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                  }}
                                >
                                  {student.firstname?.[0]}{student.lastname?.[0]} 
                                </Avatar>
                              </TableCell>
                            )}

                            <TableCell>
                              <Typography variant="body2" fontWeight="500" sx={{ color: "#1e3a5f" }}>
                                {student.studentNumber}
                                {/* {`${baseUrl()}assets/img/students/${student.studentNumber}.jpg`} */}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {student.lastname} {student.firstname} {student.othername || ""}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={student.gender}
                                size="small"
                                sx={{
                                  bgcolor: getGenderColor(student.gender),
                                  color: "white",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  minWidth: 60,
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={student.schoolType}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  borderColor: "#d1d5db",
                                }}
                              />
                            </TableCell>

                            {viewOption === 'with-dob' && (
                              <TableCell>
                                <Typography variant="body2" sx={{ color: "#4b5563" }}>
                                  {(student.dateOfBirth)}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: "monospace",
                                  bgcolor: "#f3f4f6",
                                  p: 0.5,
                                  borderRadius: 1,
                                  display: "inline-block",
                                }}
                              >
                                {Number(student.religiousType) == 1 || student.religiousType == 'Christian' ? 'Christian' : 'Islam'}
                              </Typography>
                            </TableCell>  
                            {/* <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: "monospace",
                                  bgcolor: "#f3f4f6",
                                  p: 0.5,
                                  borderRadius: 1,
                                  display: "inline-block",
                                }}
                              >
                                {student.accCode || "N/A"}
                              </Typography>
                            </TableCell> */}

                            <TableCell align="center">
                              <Tooltip title="View Details" arrow>
                                <ActionButton
                                  color="primary"
                                  onClick={() => handleViewStudent(student)}
                                  size="small"
                                >
                                  <Visibility fontSize="small" />
                                </ActionButton>
                              </Tooltip>

                              <Tooltip title="Edit Student" arrow>
                                <ActionButton
                                  color="secondary"
                                  onClick={() => handleEditStudent(student)}
                                  size="small"
                                >
                                  <Edit fontSize="small" />
                                </ActionButton>
                              </Tooltip>

                              <Tooltip title="Delete Student" arrow>
                                <ActionButton
                                  color="error"
                                  onClick={() => handleDeleteStudent(student.id)}
                                  size="small"
                                >
                                  <Delete fontSize="small" />
                                </ActionButton>
                              </Tooltip>
                            </TableCell>
                          </StyledTableRow>
                        ))}

                        {students.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={viewOption === 'with-passport' ? 8 : viewOption === 'with-dob' ? 7 : 6} align="center" sx={{ py: 6 }}>
                              <Box sx={{ textAlign: "center" }}>
                                <School sx={{ fontSize: 48, color: "#d1d5db", mb: 2 }} />
                                <Typography color="text.secondary" gutterBottom variant="h6">
                                  No students registered yet
                                </Typography>
                                <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                  Get started by registering your first student
                                </Typography>
                                {registrationStatus === 1 ? (
                                  <Button
                                    variant="contained"
                                    startIcon={<PersonAdd />}
                                    onClick={handleRegisterStudent}
                                    sx={{ bgcolor: "#1e3a5f" }}
                                  >
                                    Register First Student
                                  </Button>
                                ) : (
                                  <Alert severity="warning" sx={{ bgcolor: '#fff4e5', color: '#7c4d00', borderColor: '#ffe0b2', mt: 1 }}>
                                    Registration is currently closed. New students cannot be registered.
                                  </Alert>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* UPDATED: Registration Modal with compact score inputs */}
              <RegistrationModal
                open={registerModalOpen}
                onClose={() => setRegisterModalOpen(false)}
                onSuccess={handleRegistrationSuccess}
                schoolCode={schoolData.schoolCode}
                schoolName={schoolData.schoolName}
                lgaCode={schoolData.lgaCode}
              />

              {/* View Modal - Enhanced */}
              <Dialog 
                open={viewModalOpen} 
                onClose={() => setViewModalOpen(false)}
                maxWidth="md"
                fullWidth
                TransitionComponent={Zoom}
                transitionDuration={300}
              >
                <DialogTitle sx={{ 
                  bgcolor: "#1e3a5f", 
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 2,
                  px: 3,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <School sx={{ fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Student Details</Typography>
                  </Box>
                  <IconButton onClick={() => setViewModalOpen(false)} sx={{ color: "white" }}>
                    <Close />
                  </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 4 }}>
                  {selectedStudent && (
                    <Grid container spacing={3}>
                      {/* Photo Section */}
                      <Grid item xs={12} display="flex" justifyContent="center">
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Chip 
                              label={selectedStudent.gender} 
                              size="small"
                              sx={{ 
                                bgcolor: getGenderColor(selectedStudent.gender), 
                                color: "white",
                                fontWeight: 600,
                              }} 
                            />
                          }
                        >
                          <Avatar
                            src={  selectedStudent.passport
                                      ? `${baseUrl()}assets/${selectedStudent.passport}`
                                      : `${baseUrl()}assets/img/students/${selectedStudent.studentNumber}.jpg`}
                            alt={`${selectedStudent.firstname} ${selectedStudent.lastname}`}
                            sx={{ 
                              width: 140, 
                              height: 140, 
                              border: "4px solid #e5e7eb",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                            }}
                          >
                            {selectedStudent.firstname?.[0]}{selectedStudent.lastname?.[0]}
                          </Avatar>
                        </Badge>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider>
                          <Chip label="Personal Information" size="small" sx={{ fontWeight: 600 }} />
                        </Divider>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Full Name</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#f9fafb" }}>
                          <Typography variant="body1" fontWeight="500">
                            {selectedStudent.lastname} {selectedStudent.firstname} {selectedStudent.othername}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Student Number</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#f9fafb" }}>
                          <Typography variant="body1" fontWeight="500">{selectedStudent.studentNumber}</Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Access Code</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#f9fafb" }}>
                          <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{selectedStudent.accCode}</Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Date of Birth</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#f9fafb" }}>
                          <Typography variant="body1">
                            {(selectedStudent.dateOfBirth)}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }}>
                          <Chip label="Academic Scores" size="small" sx={{ fontWeight: 600 }} />
                        </Divider>
                      </Grid>

                      <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                              <TableRow>
                                <TableCell><b>Subject</b></TableCell>
                                <TableCell align="center"><b>Term 1</b></TableCell>
                                <TableCell align="center"><b>Term 2</b></TableCell>
                                <TableCell align="center"><b>Term 3</b></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>English Studies</TableCell>
                                <TableCell align="center">{selectedStudent.englishTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.englishTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.englishTerm3 || "-"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Arithmetic</TableCell>
                                <TableCell align="center">{selectedStudent.arithmeticTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.arithmeticTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.arithmeticTerm3 || "-"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>General Paper</TableCell>
                                <TableCell align="center">{selectedStudent.generalTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.generalTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.generalTerm3 || "-"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Religious Education ({selectedStudent.religiousType || "N/A"})</TableCell>
                                <TableCell align="center">{selectedStudent.religiousTerm1 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.religiousTerm2 || "-"}</TableCell>
                                <TableCell align="center">{selectedStudent.religiousTerm3 || "-"}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  )}
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
                  <Button 
                    onClick={() => setViewModalOpen(false)} 
                    variant="contained"
                    sx={{ 
                      bgcolor: "#1e3a5f", 
                      "&:hover": { bgcolor: "#0f2a44" },
                      textTransform: "none",
                      px: 4,
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Edit Modal - Enhanced with Image Upload */}
              <Dialog 
                open={editModalOpen} 
                onClose={() => setEditModalOpen(false)} 
                maxWidth="md" 
                fullWidth
                TransitionComponent={Zoom}
                transitionDuration={300}
              >
                <DialogTitle sx={{ 
                  bgcolor: "#1e3a5f", 
                  color: "white",
                  py: 2,
                  px: 3,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Edit sx={{ fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit Student Record</Typography>
                  </Box>
                </DialogTitle>
                
                <DialogContent sx={{ p: 4 }}>
                  {selectedStudent && (
                    <Stack spacing={3}>
                      {/* Image Upload Section */}
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 3, 
                          borderRadius: 2,
                          bgcolor: "#f9fafb",
                          border: "2px dashed #d1d5db",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: "#1e3a5f", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                          <PhotoCamera fontSize="small" /> Student Photograph
                        </Typography>
                        
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                          <Box sx={{ position: "relative" }}>
                            <Avatar
                              src={  selectedStudent.passport
                                                    ? `${baseUrl()}assets/${selectedStudent.passport}`
                                                    : `${baseUrl()}assets/img/students/${selectedStudent.studentNumber}.jpg`}
                              alt="Student"
                              sx={{ 
                                width: 100, 
                                height: 100,
                                border: "3px solid white",
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                              }}
                            >
                              {editForm.firstname?.[0]}{editForm.lastname?.[0]}
                            </Avatar>
                            {imagePreview && (
                              <IconButton
                                size="small"
                                onClick={handleRemoveImage}
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  bgcolor: "#ef4444",
                                  color: "white",
                                  "&:hover": { bgcolor: "#dc2626" },
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                }}
                              >
                                <RemoveCircleOutline fontSize="small" />
                              </IconButton>
                            )}
                          </Box>

                          <Box>
                            <input
                              accept="image/jpeg,image/png,image/gif"
                              style={{ display: 'none' }}
                              id="edit-passport-upload"
                              type="file"
                              onChange={handleFileChange}
                            />
                            <label htmlFor="edit-passport-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUpload />}
                                sx={{
                                  borderColor: "#d1d5db",
                                  color: "#374151",
                                  "&:hover": {
                                    borderColor: "#1e3a5f",
                                    backgroundColor: "#f1f5f9",
                                  },
                                }}
                              >
                                Change Photo
                              </Button>
                            </label>
                            <Typography variant="caption" display="block" sx={{ color: "#9ca3af", mt: 1 }}>
                              JPG, PNG or GIF. Max 2MB.
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>

                      {/* Personal Information */}
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: "#1e3a5f", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                          <School fontSize="small" /> Personal Information
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              label="First Name" 
                              value={editForm.firstname} 
                              onChange={(e) => handleEditChange("firstname", e.target.value)} 
                              fullWidth
                              size="small"
                              variant="outlined"
                              InputProps={{
                                sx: { borderRadius: 1.5 }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              label="Last Name" 
                              value={editForm.lastname} 
                              onChange={(e) => handleEditChange("lastname", e.target.value)} 
                              fullWidth
                              size="small"
                              variant="outlined"
                              InputProps={{
                                sx: { borderRadius: 1.5 }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              label="Other Name" 
                              value={editForm.othername} 
                              onChange={(e) => handleEditChange("othername", e.target.value)} 
                              fullWidth
                              size="small"
                              variant="outlined"
                              InputProps={{
                                sx: { borderRadius: 1.5 }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              label="Student Number" 
                              value={editForm.studentNumber} 
                              InputProps={{ readOnly: true }}
                              fullWidth
                              size="small"
                              variant="outlined"
                              sx={{
                                "& .MuiInputBase-input": {
                                  bgcolor: "#f3f4f6",
                                  borderRadius: 1.5
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Gender</InputLabel>
                              <Select
                                value={editForm.gender}
                                label="Gender"
                                onChange={(e: SelectChangeEvent) => handleEditChange("gender", e.target.value)}
                                sx={{ borderRadius: 1.5 }}
                              >
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>School Type</InputLabel>
                              <Select
                                value={editForm.schoolType}
                                label="School Type"
                                onChange={(e: SelectChangeEvent) => handleEditChange("schoolType", e.target.value)}
                                sx={{ borderRadius: 1.5 }}
                              >
                                <MenuItem value="Private">Private</MenuItem>
                                <MenuItem value="Public">Public</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              label="Date of Birth" 
                              type="date" 
                              value={
                                editForm.dateOfBirth
                                  ? editForm.dateOfBirth.split("/").reverse().join("-")
                                  : ""
                              }
                              onChange={(e) => handleEditChange("dateOfBirth", e.target.value)} 
                              InputLabelProps={{ shrink: true }} 
                              fullWidth
                              size="small"
                              variant="outlined"
                              InputProps={{
                                sx: { borderRadius: 1.5 }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Academic Scores */}
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: "#1e3a5f", mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                          <VerifiedUser fontSize="small" /> Academic Performance
                        </Typography>

                        {/* English Scores */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1.5, fontWeight: 600 }}>
                            English Studies
                          </Typography>
                          <Grid container spacing={2}>
                            {[1, 2, 3].map((term) => (
                              <Grid item xs={4} key={`english-${term}`}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <Typography variant="caption" sx={{ color: "#9ca3af", mb: 0.5 }}>
                                    Term {term}
                                  </Typography>
                                  <TextField
                                    value={editForm[`englishTerm${term}` as keyof EditFormData]}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || (/^\d+$/.test(val) && parseInt(val) <= 99)) {
                                        handleEditChange(`englishTerm${term}` as keyof EditFormData, val);
                                      }
                                    }}
                                    type="text"
                                    inputProps={{ 
                                      maxLength: 2,
                                      pattern: "[0-9]*",
                                      inputMode: "numeric",
                                      style: { 
                                        textAlign: "center",
                                        padding: "8px 4px",
                                      }
                                    }}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      width: "70px",
                                      "& .MuiInputBase-root": {
                                        borderRadius: 1.5,
                                      },
                                      "& input": {
                                        textAlign: "center",
                                        fontSize: "0.95rem",
                                        fontWeight: 500,
                                      }
                                    }}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>

                        {/* Arithmetic Scores */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1.5, fontWeight: 600 }}>
                            Arithmetic
                          </Typography>
                          <Grid container spacing={2}>
                            {[1, 2, 3].map((term) => (
                              <Grid item xs={4} key={`arithmetic-${term}`}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <Typography variant="caption" sx={{ color: "#9ca3af", mb: 0.5 }}>
                                    Term {term}
                                  </Typography>
                                  <TextField
                                    value={editForm[`arithmeticTerm${term}` as keyof EditFormData]}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || (/^\d+$/.test(val) && parseInt(val) <= 99)) {
                                        handleEditChange(`arithmeticTerm${term}` as keyof EditFormData, val);
                                      }
                                    }}
                                    type="text"
                                    inputProps={{ 
                                      maxLength: 2,
                                      pattern: "[0-9]*",
                                      inputMode: "numeric",
                                      style: { 
                                        textAlign: "center",
                                        padding: "8px 4px",
                                      }
                                    }}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      width: "70px",
                                      "& .MuiInputBase-root": {
                                        borderRadius: 1.5,
                                      },
                                      "& input": {
                                        textAlign: "center",
                                        fontSize: "0.95rem",
                                        fontWeight: 500,
                                      }
                                    }}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>

                        {/* General Paper Scores */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1.5, fontWeight: 600 }}>
                            General Paper
                          </Typography>
                          <Grid container spacing={2}>
                            {[1, 2, 3].map((term) => (
                              <Grid item xs={4} key={`general-${term}`}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <Typography variant="caption" sx={{ color: "#9ca3af", mb: 0.5 }}>
                                    Term {term}
                                  </Typography>
                                  <TextField
                                    value={editForm[`generalTerm${term}` as keyof EditFormData]}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || (/^\d+$/.test(val) && parseInt(val) <= 99)) {
                                        handleEditChange(`generalTerm${term}` as keyof EditFormData, val);
                                      }
                                    }}
                                    type="text"
                                    inputProps={{ 
                                      maxLength: 2,
                                      pattern: "[0-9]*",
                                      inputMode: "numeric",
                                      style: { 
                                        textAlign: "center",
                                        padding: "8px 4px",
                                      }
                                    }}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      width: "70px",
                                      "& .MuiInputBase-root": {
                                        borderRadius: 1.5,
                                      },
                                      "& input": {
                                        textAlign: "center",
                                        fontSize: "0.95rem",
                                        fontWeight: 500,
                                      }
                                    }}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>

                        {/* Religious Education */}
                        <Box>
                          <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1.5, fontWeight: 600 }}>
                            Religious Education
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={editForm.religiousType}
                                  label="Type"
                                  onChange={(e: SelectChangeEvent) => handleEditChange("religiousType", e.target.value)}
                                  sx={{ borderRadius: 1.5 }}
                                >
                                  <MenuItem value="Islam">Islam</MenuItem>
                                  <MenuItem value="Christian">Christian</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {[1, 2, 3].map((term) => (
                              <Grid item xs={4} sm={3} key={`religious-${term}`}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <Typography variant="caption" sx={{ color: "#9ca3af", mb: 0.5 }}>
                                    Term {term}
                                  </Typography>
                                  <TextField
                                    value={editForm[`religiousTerm${term}` as keyof EditFormData]}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || (/^\d+$/.test(val) && parseInt(val) <= 99)) {
                                        handleEditChange(`religiousTerm${term}` as keyof EditFormData, val);
                                      }
                                    }}
                                    type="text"
                                    inputProps={{ 
                                      maxLength: 2,
                                      pattern: "[0-9]*",
                                      inputMode: "numeric",
                                      style: { 
                                        textAlign: "center",
                                        padding: "8px 4px",
                                      }
                                    }}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      width: "70px",
                                      "& .MuiInputBase-root": {
                                        borderRadius: 1.5,
                                      },
                                      "& input": {
                                        textAlign: "center",
                                        fontSize: "0.95rem",
                                        fontWeight: 500,
                                      }
                                    }}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Paper>
                    </Stack>
                  )}
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                  <Button 
                    onClick={() => setEditModalOpen(false)} 
                    variant="outlined"
                    startIcon={<Cancel />}
                    sx={{ 
                      textTransform: "none",
                      borderColor: "#d1d5db",
                      color: "#374151",
                      "&:hover": {
                        borderColor: "#9ca3af",
                        backgroundColor: "#f3f4f6",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleEditSubmit} 
                    variant="contained" 
                    startIcon={uploadLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : <Save />}
                    disabled={uploadLoading}
                    sx={{ 
                      bgcolor: "#1e3a5f", 
                      "&:hover": { bgcolor: "#0f2a44" },
                      textTransform: "none",
                      px: 4,
                    }}
                  >
                    {uploadLoading ? "Saving..." : "Save Changes"}
                  </Button>
                </DialogActions>
              </Dialog>
            </Container>

            {/* FOOTER - Enhanced with professional styling */}
            <Box component="footer" sx={{ bgcolor: "#1e3a5f", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5, opacity: 0.8 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5, opacity: 0.8 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", opacity: 0.8 }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5, opacity: 0.8 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5, opacity: 0.8 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", opacity: 0.8 }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default RegisteredStudents;